import React, { useState, useRef, useEffect, useCallback, useImperativeHandle } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Box, Typography, IconButton, Chip, Tooltip } from '@material-ui/core';
import MicIcon from '@material-ui/icons/Mic';
import StopIcon from '@material-ui/icons/Stop';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import PauseIcon from '@material-ui/icons/Pause';
import DeleteIcon from '@material-ui/icons/Delete';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';

const MAX_RECORDING_SECONDS = 300; // 5 minutes
const WARNING_SECONDS = 240; // 4 minutes

/**
 * Returns the best supported MediaRecorder MIME type for the current browser.
 *
 * Priority:
 *   1. audio/webm;codecs=opus — Chrome, Firefox, Edge, Android Chrome
 *   2. audio/webm             — Chromium fallback
 *   3. audio/ogg;codecs=opus  — Firefox desktop
 *   4. audio/mp4;codecs=mp4a  — Safari on macOS / iOS 14.3+
 *   5. audio/mp4              — Safari fallback
 *   6. ''                     — Let the browser pick its own default
 */
function getSupportedMimeType() {
  if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') {
    return '';
  }
  const types = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/mp4;codecs=mp4a.40.2',
    'audio/mp4',
  ];
  return types.find((t) => MediaRecorder.isTypeSupported(t)) || '';
}

const useStyles = makeStyles((theme) => ({
  recordingContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 1),
    borderRadius: 6,
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    marginTop: theme.spacing(0.5),
  },
  recordingDot: {
    color: '#ef4444',
    fontSize: '0.7rem',
    animation: '$pulse 1.5s infinite',
  },
  recordingTimer: {
    fontFamily: 'monospace',
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#dc2626',
    minWidth: 44,
  },
  clipContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.5, 1),
    borderRadius: 6,
    backgroundColor: '#f0f9ff',
    border: '1px solid #bae6fd',
    marginTop: theme.spacing(0.5),
  },
  clipLabel: {
    fontSize: '0.75rem',
    color: '#0369a1',
  },
  '@keyframes pulse': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0.3 },
  },
}));

const formatTime = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

/**
 * AudioRecorder component for offline audio recording.
 * Records audio via MediaRecorder API (no network required).
 *
 * Props:
 *   onRecordingComplete(blob, duration) - Called when recording finishes
 *   disabled - Whether recording is disabled
 *   recordings - Array of existing recordings for this field [{id, duration, transcribed, transcription, audioBlob}]
 *   onDeleteRecording(recordId) - Delete a recording
 *   onPlayRecording(recordId) - Play a recording
 */
const AudioRecorder = React.forwardRef(({ onRecordingComplete, disabled = false, recordings = [], onDeleteRecording, hideRecordButton = false }, ref) => {
  const classes = useStyles();
  const [isRecording, setIsRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [playingId, setPlayingId] = useState(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioRef = useRef(null);
  const streamRef = useRef(null);
  const elapsedRef = useRef(0);
  const isMountedRef = useRef(true);

  // Release the microphone stream (idempotent).
  const releaseStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setIsRecording(false);
  }, []);

  const startRecording = useCallback(async () => {
    // Guard: some mobile browsers, WebViews, or insecure (non-HTTPS) contexts do
    // not expose navigator.mediaDevices / MediaRecorder. Bail out gracefully
    // instead of throwing a TypeError.
    if (
      typeof navigator === 'undefined' ||
      !navigator.mediaDevices ||
      typeof navigator.mediaDevices.getUserMedia !== 'function' ||
      typeof MediaRecorder === 'undefined'
    ) {
      console.warn('Audio recording is not supported on this device or browser.');
      return;
    }

    let stream = null;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // The component may have unmounted (e.g. Reset pressed) while the mic
      // permission prompt was pending. Release the mic and abort so we don't
      // leave a dangling stream that blocks the next getUserMedia call.
      if (!isMountedRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      streamRef.current = stream;
      const mimeType = getSupportedMimeType();
      const mediaRecorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mediaRecorder.mimeType });
        const duration = elapsedRef.current;
        chunksRef.current = [];
        // Always release the microphone tracks.
        releaseStream();

        if (isMountedRef.current && onRecordingComplete && blob.size > 0) {
          onRecordingComplete(blob, duration);
        }
        if (isMountedRef.current) setElapsed(0);
      };

      mediaRecorder.start(1000); // collect data every second
      setIsRecording(true);
      setElapsed(0);
      elapsedRef.current = 0;

      timerRef.current = setInterval(() => {
        setElapsed((prev) => {
          const next = prev + 1;
          elapsedRef.current = next;
          if (next >= MAX_RECORDING_SECONDS) {
            stopRecording();
          }
          return next;
        });
      }, 1000);
    } catch (error) {
      // Permission denied, device busy (NotReadableError/AbortError on mobile),
      // or unsupported codec. Release any partially-acquired stream so the mic
      // isn't left on, and reset local state.
      console.error('Failed to start recording:', error);
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
      streamRef.current = null;
      if (isMountedRef.current) {
        setIsRecording(false);
        setElapsed(0);
      }
    }
  }, [onRecordingComplete, releaseStream, stopRecording]);

  // Expose start/stop to parent via ref (used by SpeechTextArea's unified Dictate button)
  useImperativeHandle(ref, () => ({
    startRecording,
    stopRecording,
  }), [startRecording, stopRecording]);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      try {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
          mediaRecorderRef.current.stop();
        }
      } catch (e) {
        // Recorder may already be torn down on some mobile browsers — ignore.
      }
      // Release the microphone even if onstop never fires (unmount mid-recording,
      // e.g. Reset pressed). This prevents the mic staying locked and breaking the
      // next recording after the form reloads.
      releaseStream();
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [releaseStream]);

  const handlePlayToggle = (recording) => {
    if (playingId === recording.id) {
      // Stop playing
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlayingId(null);
    } else {
      // Start playing
      if (audioRef.current) {
        audioRef.current.pause();
      }
      const url = URL.createObjectURL(recording.audioBlob);
      const audio = new Audio(url);
      audio.onended = () => {
        setPlayingId(null);
        URL.revokeObjectURL(url);
      };
      audio.play();
      audioRef.current = audio;
      setPlayingId(recording.id);
    }
  };

  return (
    <Box>
      {/* Active Recording Indicator */}
      {isRecording && (
        <Box className={classes.recordingContainer}>
          <FiberManualRecordIcon className={classes.recordingDot} />
          <Typography className={classes.recordingTimer}>{formatTime(elapsed)}</Typography>
          {elapsed >= WARNING_SECONDS && (
            <Typography variant="caption" style={{ color: '#dc2626', fontSize: '0.7rem' }}>
              Max {formatTime(MAX_RECORDING_SECONDS)}
            </Typography>
          )}
          <Tooltip title="Stop Recording">
            <IconButton size="small" onClick={stopRecording} style={{ color: '#dc2626' }}>
              <StopIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* Saved Recording Clips */}
      {recordings.map((rec) => (
        <Box key={rec.id} className={classes.clipContainer}>
          <Tooltip title={playingId === rec.id ? 'Pause' : 'Play'}>
            <IconButton size="small" onClick={() => handlePlayToggle(rec)} style={{ color: '#0369a1' }}>
              {playingId === rec.id ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Chip
            size="small"
            icon={<MicIcon style={{ fontSize: 14 }} />}
            label={
              rec.transcribed
                ? `Transcribed (${formatTime(rec.duration)})`
                : `${formatTime(rec.duration)} — will transcribe when online`
            }
            style={{
              backgroundColor: rec.transcribed ? '#dcfce7' : '#e0f2fe',
              color: rec.transcribed ? '#166534' : '#0369a1',
              fontSize: '0.7rem',
            }}
          />
          {onDeleteRecording && (
            <Tooltip title="Delete recording">
              <IconButton size="small" onClick={() => onDeleteRecording(rec.id)} style={{ color: '#94a3b8' }}>
                <DeleteIcon style={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ))}

      {/* Record Button — hidden when parent controls recording via ref (e.g. unified Dictate button) */}
      {!isRecording && !disabled && !hideRecordButton && (
        <Tooltip title="Record audio (offline)">
          <IconButton size="small" onClick={startRecording} style={{ color: '#dc2626', marginTop: 4 }}>
            <MicIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
});

export default AudioRecorder;
